import { stringify } from '@domonda/query-params';
import { bludeltaRequest, BluDeltaResponse } from './network';
import { InvoiceContainer, InvoiceContainerWithoutInvoiceDetections } from './invoiceContainer';
import { filterCache } from '../../../filterCache';

// filter

export interface FilterInvoiceContainersParams<IncludeInvoiceDetections extends boolean = false> {
  /**
   * documentState `autoprocessed`:
   * Retrieve a list of invoice containers which have been automatically processed
   * correctly by BLU DELTA SMB and therefore do NOT require manual validation. The
   * objects returned are on container level, so each object can contain one or more
   * invoices. OCR texts are not part of the result to avoid slowing down the system.
   *
   * documentState `needForAction`:
   * Retrieve a list of invoice containers which require manual validation.
   * The objects returned are on container level, so each object can contain
   * one or more invoices. OCR texts are not part of the result to avoid
   * slowing down the system.
   *
   * documentState `validated`:
   * Retrieve a list of invoice containers which are validated and ready to be released.
   *
   * documentState `error`:
   * Retrieve a list of invoice containers which are in an erroneous state.
   */
  documentState: 'autoprocessed' | 'needForAction' | 'validated' | 'error' | 'New,Autoprocessed,NeedForAction,Validated,Released,Downloaded,Error,Deleted,Confirmed';
  fullTextQuery?: string | null;
  includeDetectInvoiceResponse: IncludeInvoiceDetections extends true ? true : (false | undefined);
  orderBy?: string | null;
  orderDescending?: boolean | null;
}

export interface FilterInvoiceContainersResponse<IncludeInvoiceDetections extends boolean = false>
  extends BluDeltaResponse {
  From: Time;
  To: Time;
  TotalPageCount: number;
  InvoiceContainers: IncludeInvoiceDetections extends true
    ? InvoiceContainer[]
    : InvoiceContainerWithoutInvoiceDetections[];
}

export async function filterInvoiceContainers<IncludeInvoiceDetections extends boolean = false>(
  params: FilterInvoiceContainersParams<IncludeInvoiceDetections>,
) {
  const queryParams = stringify(params, { prependQuestionMark: true });
  // const cachedQuery = filterCache.get(queryParams);
  // if (cachedQuery) return cachedQuery;
  const result = await bludeltaRequest<FilterInvoiceContainersResponse<IncludeInvoiceDetections>>(
    'GET',
    `/filter/invoices${queryParams}`,
  );
  normalizeInvoiceContainers(result.InvoiceContainers);
  filterCache.set(queryParams, result);
  return result;
}

// Normalizes a set of invoice containers. BEWARE: the function mutates the object.
export function normalizeInvoiceContainers(
  invoiceContainers: InvoiceContainer[] | InvoiceContainerWithoutInvoiceDetections[],
) {
  invoiceContainers.forEach((invoiceContainer) => {
    if (invoiceContainer.Invoices && invoiceContainer.Invoices.length)
      normalizeInvoiceContainer(invoiceContainer);
  });
}

// Normalizes the invoice container. BEWARE: the function mutates the object.
export function normalizeInvoiceContainer(
  invoiceContainer: InvoiceContainer | InvoiceContainerWithoutInvoiceDetections,
) {
  invoiceContainer.Invoices.forEach((invoice) => {
    invoice.Pages.sort(
      ({ PageNo: leftPageNo }, { PageNo: rightPageNo }) => leftPageNo - rightPageNo,
    );
  });
}

// validatable

export interface GetValidatableInvoiceContainersParams {
  documentState: 'autoprocessed' | 'needForAction' | 'error' | 'New,Autoprocessed,NeedForAction,Validated,Released,Downloaded,Error,Deleted,Confirmed';
  fullTextQuery: string | null;
  orderBy: string | null;
  orderDescending: boolean | null;
  PageNumber: number | null;
  PageSize: number | null;
  invoiceDetailsToQuery: string | null;
  invoiceDetailQuery: string | null;
}

//export type GetValidatableInvoiceContainersResponse = FilterInvoiceContainersResponse<false>;

//we need the prediction details for defail based search
export type GetValidatableInvoiceContainersResponse = FilterInvoiceContainersResponse<true>;

export function getValidatableInvoiceContainers(params: GetValidatableInvoiceContainersParams) {
  return filterInvoiceContainers<false>({
    ...params,
    includeDetectInvoiceResponse: false,
  });
}

// releasable

export interface GetReleasableInvoiceContainersParams {
  documentState: 'autoprocessed' | 'validated';
}

export type GetReleasableInvoiceContainersResponse = FilterInvoiceContainersResponse<true>;

export function getReleasableInvoiceContainers(params: GetReleasableInvoiceContainersParams) {
  return filterInvoiceContainers<true>({
    ...params,
    includeDetectInvoiceResponse: true,
  });
}
